import { useQuery } from '@tanstack/react-query';
import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api, type User } from '@/api/client';
import { envStyle } from '@/lib/format';
import { Link, navigate } from '@/lib/router';
import { Brand } from './Brand';
import { Icon } from './Icon';

// The modules, as pages. G then the letter goes there from the keyboard.
export const MODULES: ReadonlyArray<{ label: string; path: string; key: string }> = [
    { label: 'Explore', path: '/', key: 'e' },
    { label: 'Issues', path: '/issues', key: 'i' },
    { label: 'Patterns', path: '/patterns', key: 'p' },
    { label: 'Boards', path: '/boards', key: 'b' },
    { label: 'Alerts', path: '/alerts', key: 'a' },
    { label: 'Sources', path: '/sources', key: 's' },
];

export const moduleOf = (path: string): string =>
    path === '/' ? '/' : (MODULES.find((module) => module.path !== '/' && path.startsWith(module.path))?.path ?? path);

const SENSOR_SERVICES: ReadonlyArray<[string, string]> = [
    ['api', 'api'],
    ['cron', 'cron'],
    ['media', 'ms-media'],
];

// A menu that closes when the pointer goes down anywhere else.
function useOutside(open: boolean, close: () => void): RefObject<HTMLDivElement | null> {
    const root = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDown = (event: MouseEvent): void => {
            if (!root.current?.contains(event.target as Node)) {
                close();
            }
        };
        window.addEventListener('mousedown', onDown);

        return () => window.removeEventListener('mousedown', onDown);
    }, [open, close]);

    return root;
}

// The environment on screen: a pill in its colour (the edge across the window is the same colour), and the menu that
// switches it. E cycles from the keyboard.
function EnvPicker(props: { env: string; envs: readonly string[]; onEnv: (env: string) => void }): ReactNode {
    const [open, setOpen] = useState(false);
    const root = useOutside(open, () => setOpen(false));
    const style = props.env ? envStyle(props.env) : { label: '…', color: 'var(--line-strong)' };

    return (
        <div className="envpick" ref={root}>
            <button className="envpill" onClick={() => setOpen(!open)} title="The environment on screen (E switches)">
                <span className="edot" />
                <span className="elabel">{style.label}</span>
                {props.envs.length > 1 && <Icon name="chevron" size={14} />}
            </button>
            {open && props.envs.length > 0 && (
                <div className="menu float">
                    {props.envs.map((env) => (
                        <button
                            key={env}
                            className={env === props.env ? 'on' : ''}
                            onClick={() => {
                                setOpen(false);
                                if (env !== props.env) {
                                    props.onEnv(env);
                                }
                            }}
                        >
                            <i className="mdot" style={{ background: envStyle(env).color }} />
                            {envStyle(env).label}
                            {env === props.env && (
                                <span className="check">
                                    <Icon name="check" size={15} />
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Ingestion as one number, lines a second over five minutes; each service, and whether it has gone quiet, on hover.
function Ingest({ env }: { env: string }): ReactNode {
    const { data, dataUpdatedAt } = useQuery({ queryKey: ['status'], queryFn: api.status, refetchInterval: 10_000 });
    const sources = (data?.sources ?? []).filter((source) => source.env === env);
    const rows = SENSOR_SERVICES.map(([label, service]) => {
        const entries = sources.flatMap((source) => source.services.filter((item) => item.service === service));
        const perSecond = entries.reduce((sum, item) => sum + item.linesPerMinute, 0) / 60;
        const last = Math.max(0, ...entries.map((item) => Date.parse(item.lastAt)));

        return { label, service, perSecond, quiet: last === 0 || dataUpdatedAt - last > 120_000 };
    });
    const total = rows.reduce((sum, row) => sum + row.perSecond, 0);
    const quiet = rows.filter((row) => row.quiet);
    const rate = (value: number): string => value.toFixed(value < 1 ? 2 : 1);

    return (
        <Link className={`ingest ${quiet.length > 0 ? 'quiet' : ''}`} to="/sources" title="Ingestion: open Sources">
            <i />
            <b>{rate(total)}/s</b>
            <div className="ingest-pop float">
                {rows.map((row) => (
                    <div key={row.service} className="irow">
                        <i className={row.quiet ? 'quiet' : ''} />
                        <span>{row.label}</span>
                        <b>{rate(row.perSecond)}/s</b>
                    </div>
                ))}
                <div className="foot">
                    {quiet.length > 0
                        ? `${quiet.map((row) => row.label).join(', ')} wrote nothing in the last two minutes`
                        : 'Every service wrote in the last two minutes'}
                </div>
            </div>
        </Link>
    );
}

// Who is signed in, and what they can reach from anywhere: the team, their password, the shortcuts, signing out.
function UserMenu(props: { user: User; onShortcuts: () => void; onPassword: () => void; onLogout: () => void }): ReactNode {
    const [open, setOpen] = useState(false);
    const root = useOutside(open, () => setOpen(false));
    const initials = (props.user.name || props.user.email).slice(0, 2).toUpperCase();
    const pick = (action: () => void) => () => {
        setOpen(false);
        action();
    };

    return (
        <div className="usermenu" ref={root}>
            <button className={`avatar ${open ? 'on' : ''}`} title={props.user.email} onClick={() => setOpen(!open)}>
                {initials}
            </button>
            {open && (
                <div className="menu umenu float">
                    <div className="who">
                        <b>{props.user.name || props.user.email.split('@')[0]}</b>
                        <span>{props.user.email}</span>
                        <span className="role">{props.user.role === 'admin' ? 'Admin' : 'Member'}</span>
                    </div>
                    {props.user.role === 'admin' && <button onClick={pick(() => navigate('/team'))}>Team</button>}
                    <button onClick={pick(() => navigate('/sources'))}>Sources</button>
                    <button onClick={pick(props.onPassword)}>Change my password</button>
                    <button onClick={pick(props.onShortcuts)}>
                        Keyboard shortcuts{' '}
                        <span className="kbd" style={{ marginLeft: 'auto' }}>
                            ?
                        </span>
                    </button>
                    <div className="sep" />
                    <button className="danger" onClick={props.onLogout}>
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

export function TopBar(props: {
    path: string;
    env: string;
    envs: readonly string[];
    onEnv: (env: string) => void;
    privacy: boolean;
    onPrivacy: (privacy: boolean) => void;
    user: User;
    // Rules firing now: a badge on Alerts.
    firing: number;
    onFind: () => void;
    onLogout: () => void;
    onShortcuts: () => void;
    onPassword: () => void;
}): ReactNode {
    const active = moduleOf(props.path);
    // The pill under the active module slides to the next one: placed on the DOM, first without a transition.
    const nav = useRef<HTMLElement>(null);
    const mark = useRef<HTMLSpanElement>(null);
    useLayoutEffect(() => {
        const element = nav.current;
        const pill = mark.current;
        if (!element || !pill) {
            return;
        }
        const place = (): void => {
            const on = element.querySelector<HTMLElement>('a.on');
            pill.style.opacity = on ? '1' : '0';
            if (on) {
                pill.style.transform = `translateX(${on.offsetLeft}px)`;
                pill.style.width = `${on.offsetWidth}px`;
            }
        };
        place();
        const ready = setTimeout(() => {
            pill.dataset.ready = '1';
        }, 60);
        const observer = new ResizeObserver(place);
        observer.observe(element);

        return () => {
            clearTimeout(ready);
            observer.disconnect();
        };
    }, [active, props.firing]);

    return (
        <header className="topbar">
            <Link to="/" className="brand-link" title="Explore">
                <Brand />
            </Link>
            <EnvPicker env={props.env} envs={props.envs} onEnv={props.onEnv} />
            {props.privacy && (
                <span className="privatepill" title="Privacy mode: addresses, emails and people's ids are masked (P)">
                    <Icon name="shield" size={14} />
                    Private
                </span>
            )}
            <nav className="modules" ref={nav}>
                <span className="navline" ref={mark} />
                {MODULES.map((module) => (
                    <Link
                        key={module.path}
                        to={module.path}
                        className={module.path === active ? 'on' : ''}
                        title={`G then ${module.key.toUpperCase()}`}
                    >
                        {module.label}
                        {module.path === '/alerts' && props.firing > 0 && (
                            <span className="navbadge" title={`${props.firing} ${props.firing === 1 ? 'rule fires' : 'rules fire'} now`}>
                                {props.firing}
                            </span>
                        )}
                    </Link>
                ))}
            </nav>
            <span className="spacer" />
            <Ingest env={props.env} />
            <button className="find" onClick={props.onFind} title="Find an exception, a request or a user id; run a command (⌘K)">
                <Icon name="search" size={15} />
                <span>Find anything</span>
                <span className="kbd">⌘K</span>
            </button>
            <button
                className={`iconbtn privacy ${props.privacy ? 'on' : ''}`}
                onClick={() => props.onPrivacy(!props.privacy)}
                title={props.privacy ? 'Privacy mode is on: people masked (P turns it off)' : 'Privacy mode, for screen shares (P)'}
            >
                <Icon name={props.privacy ? 'shield' : 'eye'} size={17} />
            </button>
            <UserMenu user={props.user} onShortcuts={props.onShortcuts} onPassword={props.onPassword} onLogout={props.onLogout} />
        </header>
    );
}
